import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { GROUPS, SOLUTIONS, SOLUTIONS_IMAGE, SITE } from "@/config/site";

export default function Solutions() {
  return (
    <>
      <Seo
        title="Solutions — Seven Destinations for Business Opportunities"
        description="Explore DJC Global's seven solution destinations: healthcare revenue-cycle staffing, employee benefits, digital asset strategy, tariff recovery, technology costs, emerging technology, and AI marketing."
        path="/solutions"
      />
      <PageHero
        eyebrow="Solutions"
        title="Seven destinations. One conversation away."
        sub="DJC Global connects you with specialized providers across four groups. Every destination explains what it is, who it serves, and what to do next — before you leave our site."
      >
        <img
          src={SOLUTIONS_IMAGE}
          alt="A modern tower rising into low cloud"
          loading="lazy"
          className="mt-10 h-[260px] w-full rounded-md border border-silver/60 object-cover sm:h-[340px]"
          data-testid="solutions-hero-image"
        />
      </PageHero>

      <section className="bg-platinum pb-16 pt-4 sm:pb-24" data-testid="solutions-groups">
        <div className="mx-auto max-w-7xl space-y-14 px-4 sm:px-6 lg:px-8">
          {GROUPS.map((g, gi) => (
            <Reveal key={g.slug} delay={gi * 0.04}>
              <div>
                <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-silver/60 pb-4">
                  <div>
                    <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                      {g.name}
                    </h2>
                    <p className="mt-1 text-ink/65">{g.blurb}</p>
                  </div>
                  <span className="font-display text-sm font-bold text-silver">
                    {String(SOLUTIONS.filter((s) => s.group === g.name).length).padStart(2, "0")}{" "}
                    destination{SOLUTIONS.filter((s) => s.group === g.name).length === 1 ? "" : "s"}
                  </span>
                </div>
                <ul className="mt-6 grid gap-5 md:grid-cols-2">
                  {SOLUTIONS.filter((s) => s.group === g.name).map((s) => (
                    <li key={s.id}>
                      <Link
                        to={`/solutions/${s.slug}`}
                        data-testid={`solutions-card-${s.slug}`}
                        className="group flex h-full flex-col rounded-md border border-silver/60 bg-white p-6 shadow-sm transition-all duration-300 hover:border-cobalt hover:shadow-md"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <span className="font-display text-sm font-bold text-cobalt">{s.num}</span>
                          <ArrowUpRight className="h-4 w-4 text-silver transition-colors group-hover:text-cobalt" />
                        </div>
                        <h3 className="mt-3 font-display text-lg font-bold text-ink transition-colors group-hover:text-cobalt">
                          {s.name}
                        </h3>
                        <p className="mt-2 flex-1 text-sm leading-relaxed text-ink/70">{s.short}</p>
                        <span className="mt-4 inline-flex items-center gap-1.5 border-t border-silver/50 pt-3 text-sm font-semibold text-cobalt">
                          View Solution
                          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}

          <Reveal>
            <div className="rounded-md bg-midnight p-8 text-center sm:p-12">
              <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">
                Not sure which destination fits?
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-white/75">
                That's a normal place to start. Tell us what you'd like to improve — we'll point you
                to the right door.
              </p>
              <Link
                to="/contact"
                data-testid="solutions-contact-cta"
                className="mt-7 inline-flex items-center gap-2 rounded-md bg-white px-7 py-3.5 font-display text-sm font-semibold text-midnight transition-colors hover:bg-silver"
              >
                Talk With DJC Global
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
